import React, { useEffect, useState } from "react";
import AlertService from "../../services/alert/AlertService";
import "../../assets/css/alert.css";
import { useAlertContext } from "../../contexts/AlertContext";
import Loader from "../components/loader";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

function ListeAlert() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [deletePageChecked, setDeletePageChecked] = useState(false);

  const { lastAlert } = useAlertContext();

  const userData = JSON.parse(localStorage.getItem("user"));
  const limitOptions = [10, 20, 30, 50, 100, 200];

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const getTypeLabel = (type) => {
    switch (Number(type)) {
      case 1:
        return "Inactivité";
      case 2:
        return "Non connecté";
      case 3:
        return "Mot interdit";
      case 4:
        return "Installation logiciel";
      case 5:
        return "Navigation interdite";
      default:
        return "Inconnu";
    }
  };

  const getTypeColor = (type) => {
    switch (Number(type)) {
      case 1:
        return "#f59e0b";
      case 2:
        return "#ef4444";
      case 3:
        return "#06b6d4";
      case 4:
        return "#6b7280";
      case 5:
        return "#111827";
      default:
        return "#64748b";
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(date));
  };

  const fetchAlerts = async () => {
    if (!userData?._id) return;

    setLoading(true);

    try {
      const response = await AlertService.getAlertsBySupervisor(
        userData._id,
        currentPage,
        itemsPerPage,
        dateFilter,
      );

      setAlerts(response.data.data || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error("Erreur récupération alertes", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, [userData?._id, currentPage, itemsPerPage, dateFilter]);

  useEffect(() => {
    if (!lastAlert) return;

    setAlerts((prev) => {
      const exists = prev.some((item) => item._id === lastAlert._id);

      if (exists) {
        return prev.map((item) =>
          item._id === lastAlert._id ? lastAlert : item,
        );
      }

      return [lastAlert, ...prev];
    });
  }, [lastAlert]);

  const handleDateChange = (e) => {
    setDateFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleResetFilter = () => {
    setDateFilter("");
    setCurrentPage(1);
  };

  const handleDeleteCurrentPage = async () => {
    if (!deletePageChecked) {
      alert("Veuillez cocher la case avant de supprimer.");
      return;
    }

    if (alerts.length === 0) {
      alert("Aucune alerte à supprimer sur cette page.");
      return;
    }

    confirmAlert({
      title: "Confirmation",
      message: `Voulez-vous supprimer les ${alerts.length} lignes affichées sur cette page ?`,
      buttons: [
        {
          label: "Oui",
          onClick: async () => {
            try {
              const ids = alerts.map((alert) => alert._id);
              await AlertService.deleteAlerts(ids);

              setDeletePageChecked(false);

              if (currentPage > 1 && alerts.length === 1) {
                setCurrentPage((prev) => prev - 1);
              } else {
                fetchAlerts();
              }
            } catch (error) {
              console.error("Erreur suppression alertes", error);
              alert("Erreur pendant la suppression.");
            }
          },
        },
        { label: "Non" },
      ],
    });
  };

  const handleViewAlert = async (alert) => {
    setSelectedAlert(alert);

    if (alert.isRead === 1) {
      try {
        await AlertService.markOneAsRead(alert._id);

        setAlerts((prev) =>
          prev.map((item) =>
            item._id === alert._id ? { ...item, isRead: 2 } : item,
          ),
        );

        setSelectedAlert({ ...alert, isRead: 2 });
      } catch (error) {
        console.error("Erreur lecture alerte", error);
      }
    }
  };

  return (
    <div className="container-fluid">
      {/* TITLE */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="h4 mb-0">Liste des alertes</h2>
      </div>

      {/* FILTRES */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <input
              type="date"
              className="form-control"
              style={{ maxWidth: "220px" }}
              value={dateFilter}
              onChange={handleDateChange}
            />

            <button
              className="btn btn-outline-secondary"
              onClick={handleResetFilter}
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div className="d-flex align-items-center gap-2">
                  <span className="small text-secondary">Afficher</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "80px" }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    {limitOptions.map((limit) => (
                      <option key={limit} value={limit}>
                        {limit}
                      </option>
                    ))}
                  </select>
                  <span className="small text-secondary">lignes</span>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <div className="form-check mb-0">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="deletePageCheck"
                      checked={deletePageChecked}
                      onChange={(e) => setDeletePageChecked(e.target.checked)}
                    />
                    <label
                      className="form-check-label small"
                      htmlFor="deletePageCheck"
                    >
                      Supprimer la page affichée
                    </label>
                  </div>

                  <button
                    className="btn btn-danger btn-sm"
                    disabled={!deletePageChecked || alerts.length === 0}
                    onClick={handleDeleteCurrentPage}
                  >
                    Valider
                  </button>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Utilisateur</th>
                      <th>Société</th>
                      <th>Date</th>
                      <th>Nombre de notifications</th>
                      <th>Statut</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {alerts.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          Aucune alerte trouvée
                        </td>
                      </tr>
                    ) : (
                      alerts.map((alert) => (
                        <tr key={alert._id}>
                          <td>
                            <strong>
                              {alert.user?.nom} {alert.user?.prenom}
                            </strong>
                          </td>

                          <td>{alert.user?.entreprise?.nom || "-"}</td>

                          <td>{alert.dateJour}</td>

                          <td>
                            <span className="badge bg-primary">
                              {alert.notifications?.length || 0}
                            </span>
                          </td>

                          <td>
                            {alert.isRead === 1 ? (
                              <span className="badge bg-danger">Non lu</span>
                            ) : (
                              <span className="badge bg-success">Lu</span>
                            )}
                          </td>

                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleViewAlert(alert)}
                            >
                              Voir plus
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <button
                  className="btn btn-outline-primary btn-sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  Précédent
                </button>

                <span className="small text-secondary">
                  Page {currentPage} / {totalPages || 1}
                </span>

                <button
                  className="btn btn-outline-primary btn-sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  Suivant
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* MODAL DETAIL */}
      {selectedAlert && (
        <div
          className="modal fade show"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1055,
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            style={{ margin: 0 }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">
                    Notifications de {selectedAlert.user?.nom}{" "}
                    {selectedAlert.user?.prenom}
                  </h5>
                  <small className="text-muted">
                    Date : {selectedAlert.dateJour}
                  </small>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSelectedAlert(null)}
                ></button>
              </div>

              <div className="modal-body">
                {selectedAlert.notifications?.length === 0 ? (
                  <p className="text-muted">Aucune notification.</p>
                ) : (
                  <div className="list-group">
                    {selectedAlert.notifications.map((notif, index) => (
                      <div
                        key={index}
                        className="list-group-item"
                        style={{ marginBottom: "10px", borderRadius: "8px" }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span
                            style={{
                              background: getTypeColor(notif.type),
                              color: "white",
                              padding: "6px 10px",
                              borderRadius: "6px",
                              fontSize: "11px",
                            }}
                          >
                            {getTypeLabel(notif.type)}
                          </span>

                          <small className="text-muted">
                            Notification #{index + 1}
                          </small>
                        </div>

                        <p className="mb-1 fw-bold">{notif.message}</p>

                        {notif.type === 3 && notif.details?.keyword && (
                          <p className="mb-1">
                            <strong>Mot détecté :</strong>{" "}
                            {notif.details.keyword}
                          </p>
                        )}

                        {notif.type === 3 && notif.details?.text && (
                          <p className="mb-1">
                            <strong>Texte :</strong> {notif.details.text}
                          </p>
                        )}

                        {notif.type === 5 && (
                          <>
                            <p className="mb-1">
                              <strong>Site :</strong>{" "}
                              {notif.details?.site || "-"}
                            </p>
                            <p className="mb-1">
                              <strong>URL :</strong> {notif.details?.url || "-"}
                            </p>
                          </>
                        )}

                        {(notif.type === 1 || notif.type === 2) && (
                          <>
                            <p className="mb-1">
                              <strong>Début :</strong>{" "}
                              {notif.details?.from
                                ? formatDate(notif.details.from)
                                : "-"}
                            </p>
                            <p className="mb-1">
                              <strong>Fin :</strong>{" "}
                              {notif.details?.to
                                ? formatDate(notif.details.to)
                                : "-"}
                            </p>
                          </>
                        )}

                        {notif.type === 4 && (
                          <>
                            <p className="mb-1">
                              <strong>Action :</strong>{" "}
                              {notif.details?.action || "-"}
                            </p>

                            {Array.isArray(notif.details?.logiciels) && (
                              <ul className="mb-0">
                                {notif.details.logiciels.map((logiciel, i) => (
                                  <li key={i}>
                                    {typeof logiciel === "string"
                                      ? logiciel
                                      : logiciel?.name ||
                                        JSON.stringify(logiciel)}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setSelectedAlert(null)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ListeAlert;
